import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { useCallback, useEffect } from "react";
import { ItemProcessoFatorLista } from "../../../../domain/models/processos_riscos_item_fator_lista";
import { QualidadeItemProcessosFatorForm, QualidadeItemProcessosFatorSchema } from "../../../schemas/CadastroProcessosRiscosItem/qualidade_processos_item_fator";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedItemProcessosFator: ItemProcessoFatorLista | null;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  onClose: () => void;
  onSubmit: (e: QualidadeItemProcessosFatorForm) => void;
};

export const FatorModal = ({
  open, saving, loading, selectedItemProcessosFator,
  onClose, onSubmit, onOpenChangeHistory,
}: Props) => {

  const hookForm = useForm<QualidadeItemProcessosFatorForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(QualidadeItemProcessosFatorSchema),
    defaultValues: {
      FatorRisco: '',
      IternoExterno: '',
      Consequencia: '',
      Ocorrencia: null,
      Impacto: null,
      RiscoOportunidade: null,
      Acao: '',
      Oportunidade: '',
      Acompanhamento: '',
      IdItemProcessoFator: undefined,
      IdItemProcesso: undefined
    }
  });

  useEffect(() => {
    if (open) {
      if (selectedItemProcessosFator) {
        hookForm.reset({
          Acao: selectedItemProcessosFator.Acao,
          Consequencia: selectedItemProcessosFator.Consequencia,
          FatorRisco: selectedItemProcessosFator.FatorRisco,
          IternoExterno: selectedItemProcessosFator.IternoExterno === "Interno" ? "0" : "1",
          Acompanhamento: selectedItemProcessosFator.Acompanhamento,
          IdItemProcessoFator: selectedItemProcessosFator.IdItemProcessoFator,
          IdItemProcesso: selectedItemProcessosFator.IdItemProcesso,
          Impacto: selectedItemProcessosFator.Impacto,
          Ocorrencia: selectedItemProcessosFator.Ocorrencia,
          Oportunidade: selectedItemProcessosFator.Oportunidade,
          RiscoOportunidade: selectedItemProcessosFator.RiscoOportunidade,
        });
      } else {
        hookForm.reset({
          Acao: '',
          Consequencia: '',
          FatorRisco: '',
          IternoExterno: '0',
          Acompanhamento: '',
          Oportunidade: '',
          Impacto: 3,
          Ocorrencia: 3,
          RiscoOportunidade: 0,
          IdItemProcessoFator: undefined,
          IdItemProcesso: undefined
        });
      }
    }
  }, [hookForm, open, selectedItemProcessosFator]);

  const handleSubmit = useCallback((form: QualidadeItemProcessosFatorForm) => {
    if (!saving) {
      onSubmit(form);
      hookForm.reset();
    }
  }, [hookForm, onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);

  const handleChangeHistory = useCallback(() => {
    if (selectedItemProcessosFator) {
      onOpenChangeHistory(selectedItemProcessosFator.IdItemProcessoFator, LogTipoTabela.ITEM_PROCESSO_FATOR);
    }
  }, [selectedItemProcessosFator, onOpenChangeHistory]);


  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: hookForm.handleSubmit(handleSubmit)
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "800px", width: "100%" } }}
    >
      <DialogTitle>
        Cadastrar/Editar Fatores Internos/Externos, riscos e oportunidades
      </DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar"} arrangement="fullRow">
          <div className="flex flex-col w-full gap-4 pt-2">
            <DefaultFormInput
              name="FatorRisco"
              label="Evento"
              reactHookForm={hookForm}
              required={true}
            />
            <div className="flex items-center justify-start py-2">
              <label className="mr-4 font-medium text-gray-700 ">
                Risco ou Oportunidade?
              </label>
              <FormRadioGroupInput
                name="RiscoOportunidade"
                label=""
                readOnly={saving}
                options={[
                  { id: 0, label: "Risco" },
                  { id: 1, label: "Oportunidade" },
                ]}
                reactHookForm={hookForm}
              />
            </div>
            <div className="flex items-center justify-start py-2">
              <label className="mr-4 font-medium text-gray-700 ">
                Interno ou Externo
              </label>
              <FormRadioGroupInput
                name="IternoExterno"
                label=""
                readOnly={saving}
                options={[
                  { id: 0, label: "Interno" },
                  { id: 1, label: "Externo" },
                ]}
                reactHookForm={hookForm}
              />
            </div>

            {/* <DefaultFormInput
              name="IternoExterno"
              label="Interno/externo"
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            /> */}

            <DefaultFormInput
              name="Consequencia"
              label="Consequências e Impactos"
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            />

            <div className="flex items-center justify-start py-2">
              <label className="mr-4 font-medium text-gray-700 ">
                Probabilidade de ocorrência
              </label>
              <FormRadioGroupInput
                name="Ocorrencia"
                label=""
                readOnly={saving}
                options={[
                  { id: 0, label: "Baixo" },
                  { id: 1, label: "Medio" },
                  { id: 2, label: "Alto" },
                  { id: 3, label: "Não se aplica" },
                ]}
                reactHookForm={hookForm}
              />
            </div>

            <div className="flex items-center justify-start py-2">
              <label className="mr-4 font-medium text-gray-700 ">
                Impacto
              </label>
              <FormRadioGroupInput
                name="Impacto"
                label=""
                readOnly={saving}
                options={[
                  { id: 0, label: "Baixo" },
                  { id: 1, label: "Medio" },
                  { id: 2, label: "Alto" },
                  { id: 3, label: "Não se aplica" },
                ]}
                reactHookForm={hookForm}
              />
            </div>

            <DefaultFormInput
              name="Oportunidade"
              label="Ação preventiva"
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="Acao"
              label="Ação de contingência"
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="Acompanhamento"
              label="Resultados alcançados."
              multiline={true}
              rows={4}
              reactHookForm={hookForm}
            />
          </div>
        </FormContainer>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedItemProcessosFator) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}